import { HomeRounded as InternalIcon } from '@mui/icons-material';
import { Tooltip, type SxProps, type Theme } from '@mui/material';
import { indigo } from '@mui/material/colors';
import { usePermissions } from '@/hooks/use-permissions';

// Neutral indigo (#5c6bc0) — deliberately NOT a status color
// (online/away/offline/…), so the marker reads as an ownership label rather
// than device health.
const INTERNAL_COLOR = indigo[400];

interface InternalDeviceIconProps {
  /** Renders nothing unless true and the viewer is a super admin. */
  internal?: boolean | null;
  /** Icon size in px. Defaults to 15, matching the adjacent CopyDeviceId. */
  size?: number;
  sx?: SxProps<Theme>;
}

/**
 * Small accessible marker shown immediately next to a device identifier when the
 * device is company-owned (is_internal): a plain filled home glyph, no badge or
 * background. Hover tooltip + role="img"/aria-label announce "Internal device";
 * renders nothing for customer devices or non-super-admin viewers.
 */
export function InternalDeviceIcon({ internal, size = 15, sx }: InternalDeviceIconProps) {
  const { isSuperAdmin } = usePermissions();

  if (!internal || !isSuperAdmin) return null;
  return (
    <Tooltip title="Internal device">
      <InternalIcon
        role="img"
        aria-label="Internal device"
        sx={{ fontSize: size, color: INTERNAL_COLOR, flexShrink: 0, ...sx }}
      />
    </Tooltip>
  );
}

export default InternalDeviceIcon;
